import { omit } from 'ramda';

export const HTML_ELEMENT_FONT_SIZE = 16;

export enum ControllerFontType {
  Body300R = 'Body300R',
  Body400R = 'Body400R',
  Body500R = 'Body500R',
  Decor200R = 'Decor200R',
  Decor200M = 'Decor200M',
  Decor300R = 'Decor300R',
  Decor400R = 'Decor400R',
  Heading200M = 'Heading200M',
  Heading200B = 'Heading200B',
  Heading250M = 'Heading250M',
  Heading300B = 'Heading300B',
  Heading300H = 'Heading300H',
  Heading400R = 'Heading400R',
  Heading400B = 'Heading400B',
  Heading400H = 'Heading400H',
  Heading500H = 'Heading500H',
  Heading600H = 'Heading600H',
  Text200R = 'Text200R',
  Text300M = 'Text300M',
  Text350R = 'Text350R',
  Text400M = 'Text400M',
  Text500M = 'Text500M',
}

interface IControllerFontIn {
  fontFamily: ControllerFontFamily;
  fontSizePx: number;
  fontWeight: ControllerFontWeight;
  letterSpacing?: string;
  lineHeightPx: number;
}

interface IControllerFontInOut extends IControllerFontIn {
  fontSize: string;
  lineHeight: string;
}

export type IControllerFont = Omit<IControllerFontInOut, 'fontSizePx' | 'lineHeightPx'>;

enum ControllerFontFamily {
  MrEaves = 'mr-eaves-xl-modern, Helvetica, Arial, sans-serif',
  Roboto = 'Roboto, Helvetica, Arial, sans-serif',
  RobotoMono = '"Roboto Mono", monospace',
}

export enum ControllerFontWeight {
  Bold = 700,
  Heavy = 800,
  Medium = 500,
  Regular = 400,
  Light = 200,
}

type IControllerFontMap = {
  [key in ControllerFontType]: IControllerFont;
};

export const ControllerFont: IControllerFontMap = {
  Body300R: getFont({
    fontFamily: ControllerFontFamily.Roboto,
    fontSizePx: 12,
    fontWeight: ControllerFontWeight.Regular,
    lineHeightPx: 18,
  }),
  Body400R: getFont({
    fontFamily: ControllerFontFamily.Roboto,
    fontSizePx: 14,
    fontWeight: ControllerFontWeight.Regular,
    lineHeightPx: 20,
  }),
  Body500R: getFont({
    fontFamily: ControllerFontFamily.Roboto,
    fontSizePx: 16,
    fontWeight: ControllerFontWeight.Regular,
    lineHeightPx: 24,
  }),
  Decor200R: getFont({
    fontFamily: ControllerFontFamily.RobotoMono,
    fontSizePx: 10,
    fontWeight: ControllerFontWeight.Regular,
    lineHeightPx: 14,
  }),
  Decor200M: getFont({
    fontFamily: ControllerFontFamily.RobotoMono,
    fontSizePx: 10,
    fontWeight: ControllerFontWeight.Medium,
    lineHeightPx: 14,
  }),
  Decor300R: getFont({
    fontFamily: ControllerFontFamily.RobotoMono,
    fontSizePx: 12,
    fontWeight: ControllerFontWeight.Regular,
    lineHeightPx: 16,
  }),
  Decor400R: getFont({
    fontFamily: ControllerFontFamily.RobotoMono,
    fontSizePx: 14,
    fontWeight: ControllerFontWeight.Regular,
    lineHeightPx: 20,
  }),
  Heading200M: getFont({
    fontFamily: ControllerFontFamily.Roboto,
    fontSizePx: 14,
    fontWeight: ControllerFontWeight.Medium,
    letterSpacing: '0.02rem',
    lineHeightPx: 20,
  }),
  Heading200B: getFont({
    fontFamily: ControllerFontFamily.Roboto,
    fontSizePx: 14,
    fontWeight: ControllerFontWeight.Bold,
    lineHeightPx: 20,
  }),
  Heading250M: getFont({
    fontFamily: ControllerFontFamily.MrEaves,
    fontSizePx: 14,
    fontWeight: ControllerFontWeight.Medium,
    lineHeightPx: 20,
  }),
  Heading300B: getFont({
    fontFamily: ControllerFontFamily.MrEaves,
    fontSizePx: 16,
    fontWeight: ControllerFontWeight.Bold,
    lineHeightPx: 20,
  }),
  Heading300H: getFont({
    fontFamily: ControllerFontFamily.MrEaves,
    fontSizePx: 16,
    fontWeight: ControllerFontWeight.Heavy,
    lineHeightPx: 20,
  }),
  Heading400R: getFont({
    fontFamily: ControllerFontFamily.MrEaves,
    fontSizePx: 20,
    fontWeight: ControllerFontWeight.Regular,
    lineHeightPx: 24,
  }),
  Heading400B: getFont({
    fontFamily: ControllerFontFamily.MrEaves,
    fontSizePx: 20,
    fontWeight: ControllerFontWeight.Bold,
    lineHeightPx: 24,
  }),
  Heading400H: getFont({
    fontFamily: ControllerFontFamily.MrEaves,
    fontSizePx: 20,
    fontWeight: ControllerFontWeight.Heavy,
    lineHeightPx: 24,
  }),
  Heading500H: getFont({
    fontFamily: ControllerFontFamily.MrEaves,
    fontSizePx: 26,
    fontWeight: ControllerFontWeight.Heavy,
    lineHeightPx: 30,
  }),
  Heading600H: getFont({
    fontFamily: ControllerFontFamily.MrEaves,
    fontSizePx: 28,
    fontWeight: ControllerFontWeight.Heavy,
    lineHeightPx: 32,
  }),
  Text200R: getFont({
    fontFamily: ControllerFontFamily.Roboto,
    fontSizePx: 10,
    fontWeight: ControllerFontWeight.Regular,
    lineHeightPx: 14,
  }),
  Text300M: getFont({
    fontFamily: ControllerFontFamily.Roboto,
    fontSizePx: 12,
    fontWeight: ControllerFontWeight.Medium,
    lineHeightPx: 20,
  }),
  Text350R: getFont({
    fontFamily: ControllerFontFamily.Roboto,
    fontSizePx: 13,
    fontWeight: ControllerFontWeight.Regular,
    lineHeightPx: 16,
  }),
  Text400M: getFont({
    fontFamily: ControllerFontFamily.Roboto,
    fontSizePx: 14,
    fontWeight: ControllerFontWeight.Medium,
    lineHeightPx: 18,
  }),
  Text500M: getFont({
    fontFamily: ControllerFontFamily.Roboto,
    fontSizePx: 16,
    fontWeight: ControllerFontWeight.Medium,
    lineHeightPx: 20,
  }),
};

//
//
// helpers
//
//

function getOutFont(font: IControllerFontInOut): IControllerFont {
  const withoutSize = omit(['fontSizePx', 'lineHeightPx'], font);

  return withoutSize;
}

function getFont(font: IControllerFontIn): IControllerFont {
  return getOutFont({
    ...font,
    fontSize: pxToRem(font.fontSizePx),
    lineHeight: pxToRem(font.lineHeightPx),
  });
}

function pxToRem(px: number) {
  return `${px / HTML_ELEMENT_FONT_SIZE}rem`;
}
